import { FC, useCallback, useEffect, useState } from "react";
import { DefaultGrid } from "../../../components/grids/default/defaultGrid";
import { PluginWrapper, ToolbarWrapper } from "../../../components/plugins";
import useGridFilter, { DefaultDateGridFilter, DefaultDatePeriod } from "../../../system/hooks/useGridFilter";
import { useAppContext } from "../../../system/providers/appContextProvider";
import { usePluginContext } from "../../../system/providers/plugin";
import styles from "../../styles/index.module.scss";
import ActRejectionStyles from './styles/ActRejection.module.scss';
import { DocumentGridStateType } from "../../../@types/documents";
import { DocumentType } from "../../../@types/enumsGlobal";
import { Spinner } from "../../../components/spiner/Spinner";
import { ActRejectionDataProvider } from "../../../Services/DataProviders/ActRejectionDataProvider";
import { IActRejectionViewDTO } from "../../../libs/coreapi-dto/documents/ActRejection";
import { ActRejectionPanel } from "./components/ActRejectionPanel";
import SearchModalWindowAntd from "../../../components/modalWindows/SearchModalWindowAntd/SearchModalWindowAntd";
import { useTabsContext } from "../../../system/providers/tabsProvider";
import { DocumentSearchPanelAntd } from "../../../components/commandsPanels/documentSearchPanel/components/DocumetnSearchPanelAntd";
import { useSearchDocument } from "../../../components/commandsPanels/documentSearchPanel/hooks/useSearchDocument";
import { DatePeriodPanelV2 } from "../../../hoc/DatePeriodPanelV2/DatePeriodPanelV2";
import { IDatePeriod } from "../../../components/datePeriodPicker/datePeriodPicker";

export const ActRejectionPluginView: FC<IGridProps> = (props) => {

    const appContext = useAppContext();
    const pluginCtx = usePluginContext();
    const tabsContext = useTabsContext();

    const actRejectionDP = new ActRejectionDataProvider(appContext.coreApiService);

    const [data, setData] = useState<IActRejectionViewDTO[]>([]);
    const [totalCount, setTotalCount] = useState<number>(0);
    const [selectedItem, setSelectedItem] = useState<IGridRow | undefined>();
    const [gridFilter, dispatchGridFilter] = useGridFilter(DefaultDateGridFilter());
    const [viewState, setViewState] = useState<DocumentGridStateType>("view");
    const [isSubmitting, setIsSubmitting] = useState<boolean>(true);
    const [isSearchModalVisible, setIsSearchModalVisible] = useState(false);
    const [datePeriod, setDatePeriod] = useState<IDatePeriod>(DefaultDatePeriod);

    const { getSearchValue } = useSearchDocument({
        gridFilter: gridFilter,
        dispatchGridFilter: dispatchGridFilter
    });

    const handleDateFilterChange = useCallback((date: IDatePeriod) => {

        const filtersWithoutDates = gridFilter.columnFilters.filter(
            filter => filter.name !== "documentDate"
        );

        const newFilters: IGridColumnFilter[] = [...filtersWithoutDates];
        if (date?.endDate) {
            newFilters.push({
                name: "documentDate",
                value: date.endDate.toFormat("yyyyMMdd"),
                operator: "LessOrEq",
                invisible: true,
            } as IGridColumnFilter);
        }

        if (date?.startDate) {
            newFilters.push({
                name: "documentDate",
                value: date.startDate.toFormat("yyyyMMdd"),
                operator: "MoreOrEq",
                invisible: true,
            } as IGridColumnFilter);
        }

        dispatchGridFilter({
            type: "newColumnFilters",
            payload: newFilters
        });
    }, [dispatchGridFilter, gridFilter.columnFilters]);

    const handleDatePeriodChange = useCallback((date: IDatePeriod) => {
        setDatePeriod(date);
        handleDateFilterChange(date);
    }, [handleDateFilterChange]);

    const loadData = () => {
        setIsSubmitting(true);
        actRejectionDP.getView(gridFilter, (data, totalCount) => {
            setData(data);
            setTotalCount(totalCount);
            setIsSubmitting(false);
            setSelectedItem(undefined);
        });
    };

    useEffect(() => {
        loadData();
    }, [gridFilter]);

    useEffect(() => {
        if (viewState === 'refresh') {
            loadData();
            setViewState('view');
        }
    }, [viewState]);

    const handleEdit = () => {
        if (!selectedItem) return;

        const mnemocode = selectedItem.cells?.find((cell) => cell.propertyName === 'mnemocode')?.value as string;

        tabsContext.openChild(
            'act_rejection_creator_plugin',
            "edit",
            selectedItem.idGlobal,
            () => {
                loadData();
            },
            mnemocode,
        );
    };

    const handleSearchToggle = () => {
        setIsSearchModalVisible(!isSearchModalVisible);
        setViewState(isSearchModalVisible ? "return" : "search");
    };

    const hasActiveFilters = () => {
        if (!gridFilter?.columnFilters) return false;

        return gridFilter.columnFilters.some(filter =>
            filter.name !== "documentDate"
        );
    };

    return (
        <PluginWrapper>
            <ToolbarWrapper>
                <ActRejectionPanel
                    edit={{
                        onClick: handleEdit,
                        disabled: selectedItem === undefined || selectedItem?.isDeleted
                    }}
                    refresh={{
                        onClick: () => {
                            setViewState('refresh')
                        }
                    }}
                    search={{
                        onClick: handleSearchToggle,
                        disabled: false,
                    }}
                    hasActiveFilters={hasActiveFilters()}
                />
                <div className={ActRejectionStyles.datePeriodFilter} >
                    <DatePeriodPanelV2
                        datePeriod={datePeriod}
                        onDatePeriodChange={handleDatePeriodChange}
                    />
                </div>
                <DocumentSearchPanelAntd
                    onSubmit={getSearchValue}
                    selectedItem={[selectedItem, setSelectedItem]}
                />
            </ToolbarWrapper>
            <div className={styles.gridWrapper}>
                <div className={styles.masterGrid}>
                    {isSubmitting ? (
                        <Spinner />
                    ) : (
                        <DefaultGrid
                            showQuantity
                            separator
                            gridId={props.gridId}
                            data={data}
                            filter={gridFilter}
                            dataProvider={actRejectionDP}
                            totalCount={totalCount}
                            plugin={props.plugin}
                            selectedItem={selectedItem}
                            getView={(gridFilter, callback) => {
                                actRejectionDP.getView(gridFilter, (entities, totalCount) => {
                                    callback(entities, totalCount)
                                    setSelectedItem(undefined);
                                })
                            }}
                            onSelect={(row) => {
                                setSelectedItem(row);
                                row ? pluginCtx.masterGrid.onSelectEvent(row, DocumentType.actRejection) : pluginCtx.masterGrid.onUnselectEvent();
                            }}
                            onSort={(i) => {
                                dispatchGridFilter({ type: "sort", payload: i.propertyName });
                            }}
                            onFilterDelete={(i) => {
                                dispatchGridFilter({ type: "deleteColumnFilter", payload: i.propertyName });
                            }}
                            onPageNumberChange={(n) => {
                                dispatchGridFilter({ type: "changePageNumber", payload: { pageNumber: n } });
                            }}
                            onNumberPerPageChange={(n) => {
                                dispatchGridFilter({ type: "changeNumberPerPage", payload: { numberPerPage: n } });
                            }}
                        />
                    )}
                </div>
            </div>
            {isSearchModalVisible && (
                <SearchModalWindowAntd
                    columns={props.plugin?.columns.filter((x) => x.visibility === true) ?? []}
                    defaultFilters={gridFilter?.columnFilters}
                    search={(columnFilters) => {
                        dispatchGridFilter({ type: 'search', payload: columnFilters });
                        setViewState('refresh');
                    }}
                    cancel={handleSearchToggle}
                    open={true}
                    onCancel={handleSearchToggle}
                />
            )}
        </PluginWrapper>
    );
};
